import './AppContainer.sass';

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { appActions, appSelector } from '$store/app';
import { Button, LottieIcon, Placeholder, ScrollView } from '$uikit';
import { goBack, showModal } from '$navigation/helper';
import { Loader, ShareSheet } from '$shared/components';
import { postsActions } from '$store/posts';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { batchActions } from '$store';
import { AttachLink } from '$store/models';
import { mainActions } from '$store/main';
import { attachActions } from '$store/attach';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { Api } from '$api';
import { toastActions } from '$store/toast';

export const AppContainer: FC = () => {
  const [isLoading, setLoading] = useState(true);
  const { isInstalled, app } = useSelector(appSelector);
  const dispatch = useDispatch();
  const ref = useRef<HTMLIFrameElement>(null);
  const pendingRequest = useRef<{ requestId: number; purchaseId: number } | null>(null);
  const user = useCurrentUserInfo();
  const t = useTranslator();

  const sendResponse = useCallback(
    (requestId: number, success: boolean, data?: any, error?: string) => {
      if (!ref.current?.contentWindow) return;
      ref.current.contentWindow.postMessage(
        JSON.stringify({
          requestId,
          type: success ? 'success' : 'error',
          data,
          error,
        }),
        '*',
      );
    },
    [],
  );

  const handleInstall = useCallback(() => {
    dispatch(appActions.installApp(app!.id));
  }, [app]);

  const handleClose = useCallback(() => {
    goBack();
  }, []);

  const handleMessage = useCallback(
    (data: any) => {
      const { command, requestId } = data;

      if (command === 'createPost') {
        const formId = `post_${user!.id}`;
        dispatch(
          batchActions(
            postsActions.clearForm(formId),
            attachActions.clear(formId),
            postsActions.updateFormText({
              formId,
              text: data.text,
            }),
          ),
        );
        showModal('post');
      } else if (command === 'shareApp') {
        const link: AttachLink = {
          type: 'app',
          object: app,
        };
        dispatch(mainActions.setPopup(<ShareSheet title={t('app_share')} link={link} />));
      } else if (command === 'purchase') {
        const purchaseId = +data.purchaseId;
        pendingRequest.current = { requestId, purchaseId };

        dispatch(toastActions.setToastLoading());
        Api.get<any, any>(`/app/purchase/${purchaseId}`)
          .then((res) => {
            dispatch(toastActions.hideToast());
            dispatch(
              balancePayActions.open({
                amount: res.amount,
                action: BalancePayAction.APP_PURCHASE,
                objectId: purchaseId,
              }),
            );
          })
          .catch((e) => {
            dispatch(toastActions.setToastFail(e.message));
            pendingRequest.current = null;
          });
      }
    },
    [user, app, sendResponse],
  );

  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      if (event.source !== ref.current?.contentWindow) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.command) handleMessage(data);
      } catch (e) {}
    };

    const handlePurchaseResult = (event: Event) => {
      const { purchaseId } = (event as CustomEvent).detail;
      const currentId = pendingRequest.current?.purchaseId;
      if (currentId && currentId === purchaseId) {
        sendResponse(currentId, true);
        pendingRequest.current = null;
      }
    };

    window.addEventListener('message', handleWindowMessage);
    window.addEventListener('appPurchaseResult', handlePurchaseResult);

    return () => {
      window.removeEventListener('message', handleWindowMessage);
      window.removeEventListener('appPurchaseResult', handlePurchaseResult);
    };
  }, [handleMessage, sendResponse]);

  const handleLoad = useCallback(() => {
    setLoading(false);
  }, []);

  if (!app) {
    return <h1>{t('app_not_found')}</h1>;
  }

  if (!isInstalled) {
    return (
      <ScrollView>
        <Placeholder
          centered
          title={t('app_third_party_title')}
          icon={<LottieIcon name="game" />}
          iconSize={120}
          actions={
            <>
              <Button size="small" onClick={handleInstall} stretched>
                {t('app_open')}
              </Button>
              <Button size="small" mode="secondary" onClick={handleClose} stretched>
                {t('cancel')}
              </Button>
            </>
          }
        >
          {t('app_permissions_description')}
        </Placeholder>
      </ScrollView>
    );
  }

  return (
    <div className="AppContainer">
      {isLoading && (
        <div className="AppContainer__loader">
          <Loader />
        </div>
      )}
      <iframe
        ref={ref}
        className="AppContainer__frame"
        src={app.url}
        frameBorder={0}
        onLoad={handleLoad}
      />
    </div>
  );
};
